import { useState } from "react";
import { ChevronUp, Clock, Home, List, Loader2, MapPin, MessageCircle, PackageOpen, Printer, Star, User, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { CallButton } from "@/components/CallButton";
import { brl, fmtTime, PAY_LABELS, waLink } from "@/lib/api";
import { cn } from "@/lib/utils";
import { TYPE, agoText, isDone, lastChange, statusLabel, toneOf } from "./status";
import { DispatchBox, PickupCode } from "./DispatchBox";

export const EmptyDetail = () => (
  <div className="card-surface grid h-full min-h-[320px] place-items-center text-muted-foreground/60" data-testid="order-detail-empty">
    <span className="flex flex-col items-center gap-3"><PackageOpen className="h-20 w-20" strokeWidth={1.2} /><span className="text-lg">Nenhum pedido selecionado</span></span>
  </div>
);

const IconBtn = ({ onClick, label, testId, children }) => <button type="button" onClick={onClick} title={label} aria-label={label} className="grid h-9 w-9 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground" data-testid={testId}>{children}</button>;
const Tag = ({ k, v }) => <span className="flex overflow-hidden rounded border text-xs"><span className="bg-muted px-2 py-1">{k}</span><span className="px-2 py-1">{v}</span></span>;
const Block = ({ icon: Icon, title, children }) => <section className="space-y-1.5"><p className="flex items-center gap-2 text-sm font-semibold"><Icon className="h-4 w-4 text-muted-foreground" />{title}</p><div className="pl-6 text-sm">{children}</div></section>;
const Line = ({ l, v, strong }) => <p className={cn("flex justify-between", strong ? "font-semibold" : "text-muted-foreground")}><span>{l}</span><span className="font-mono">{v}</span></p>;

function WhereBlock({ o }) {
  if (o.type === "delivery") {
    return (
      <Block icon={MapPin} title="Entregar no endereço">
        <p className="text-muted-foreground">{o.customer.address}{o.zone ? ` · ${o.zone}` : ""}</p>
        {o.customer.lat != null && <a href={`https://www.google.com/maps?q=${o.customer.lat},${o.customer.lng}`} target="_blank" rel="noreferrer" className="text-xs font-medium text-primary underline" data-testid={`order-map-${o.number}`}>Abrir no mapa</a>}
      </Block>
    );
  }
  if (o.type === "retirada") return <Block icon={Home} title="Retirar no estabelecimento" />;
  return <Block icon={Home} title="Consumo no local"><p className="text-muted-foreground">{[o.table_number && `Mesa ${o.table_number}`, o.comanda_number && `Comanda ${o.comanda_number}`, o.ambiente].filter(Boolean).join(" · ") || "Balcão"}</p></Block>;
}

function Totals({ o }) {
  const sub = o.items.reduce((s, i) => s + (i.total || 0), 0);
  return (
    <div className="space-y-1 rounded-md border p-3 text-sm" data-testid="order-detail-totals">
      <Line l="Subtotal" v={brl(sub)} />
      {o.delivery_fee > 0 && <Line l="Entrega" v={brl(o.delivery_fee)} />}
      {o.payment_fee > 0 && <Line l="Taxa da forma de pagamento" v={brl(o.payment_fee)} />}
      {o.coupon && <Line l={`Cupom ${o.coupon.code}`} v={`- ${brl(o.coupon.saved)}`} />}
      {o.loyalty_discount > 0 && <Line l="Desconto fidelidade" v={`- ${brl(o.loyalty_discount)}`} />}
      <Line l="Total" v={brl(o.total)} strong />
      <p className="pt-1 text-xs text-muted-foreground">{PAY_LABELS[o.payment_method]}{o.change_for > 0 ? ` · troco p/ ${brl(o.change_for)}` : ""}
        {o.online && <span className={cn("ml-1 rounded px-1.5 py-0.5 text-[10px] font-semibold", o.online.status === "approved" ? "bg-emerald-500/15 text-emerald-600" : "bg-amber-500/15 text-amber-600")} data-testid={`order-online-${o.number}`}>{o.online.status === "approved" ? "PAGO ONLINE" : "aguardando pagamento"}</span>}
      </p>
    </div>
  );
}

function Ops({ o, couriers, onEdit }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <Clock className="h-4 w-4 text-muted-foreground" />Prazo
      <input type="number" defaultValue={o.eta_minutes} onBlur={(e) => Number(e.target.value) !== o.eta_minutes && onEdit(o, { eta_minutes: Number(e.target.value) })} className="h-9 w-16 rounded border bg-transparent px-2" data-testid={`order-eta-${o.number}`} />min
      {o.type === "delivery" && (
        <select value={o.courier?.id || ""} onChange={(e) => onEdit(o, { courier_id: e.target.value })} className="h-9 min-w-0 flex-1 rounded border bg-transparent px-2" data-testid={`order-courier-${o.number}`}>
          <option value="">Entregador…</option>{couriers.filter((c) => c.active).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      )}
    </div>
  );
}

const linkCls = "flex items-center justify-center gap-1 rounded-md border py-2 text-xs font-medium transition-colors";
function Extras({ o, notify, askRating, onCalled }) {
  return (
    <>
      {o.type === "delivery" && o.status === "entrega" && o.customer.phone && <a href={notify(o)} target="_blank" rel="noreferrer" className={cn(linkCls, "border-emerald-500/40 text-emerald-600 hover:bg-emerald-500/10")} data-testid={`order-notify-${o.number}`}><MessageCircle className="h-3.5 w-3.5" />Avisar cliente que saiu</a>}
      {o.rating && <p className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400" data-testid={`order-rating-${o.number}`}><Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />{o.rating.stars} de 5{o.rating.comment && <span className="text-muted-foreground"> · "{o.rating.comment}"</span>}</p>}
      {!o.rating && o.type === "delivery" && ["entregue", "concluido"].includes(o.status) && o.customer.phone && <a href={askRating(o)} target="_blank" rel="noreferrer" className={cn(linkCls, "border-amber-500/40 text-amber-600 hover:bg-amber-500/10 dark:text-amber-400")} data-testid={`order-ask-rating-${o.number}`}><Star className="h-3.5 w-3.5" />Pedir avaliação</a>}
      {o.status === "pronto" && <CallButton kind="order" item={o} code={o.number} onDone={onCalled} testId={`order-call-${o.number}`} />}
    </>
  );
}

function DetailFooter({ o, next, busy, go, onPrint }) {
  const done = isDone(o);
  return (
    <footer className="flex gap-2 border-t p-3">
      <DropdownMenu>
        <DropdownMenuTrigger asChild><Button variant="outline" className="h-12 w-14 shrink-0" aria-label="Mais ações" data-testid="order-more-btn"><ChevronUp className="h-5 w-5" /></Button></DropdownMenuTrigger>
        <DropdownMenuContent side="top" align="start">
          <DropdownMenuItem onClick={() => onPrint(o)} data-testid="order-more-reprint"><Printer className="mr-2 h-4 w-4" />Reimprimir</DropdownMenuItem>
          {!done && <DropdownMenuItem onClick={() => go("cancelado")} className="text-red-600 focus:text-red-600" data-testid={`order-cancel-${o.number}`}><X className="mr-2 h-4 w-4" />{o.status === "recebido" ? "Recusar pedido" : "Cancelar pedido"}</DropdownMenuItem>}
        </DropdownMenuContent>
      </DropdownMenu>
      {next ? (
        <Button className="h-12 flex-1 text-base font-semibold uppercase tracking-wide" disabled={busy} onClick={() => go(next[0])} data-testid={`order-advance-${o.number}`}>
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{next[1]}
        </Button>
      ) : <div className="grid h-12 flex-1 place-items-center rounded-md bg-muted text-sm text-muted-foreground" data-testid="order-finished">{o.status === "cancelado" ? "Pedido cancelado" : "Pedido finalizado"}</div>}
    </footer>
  );
}

export function OrderDetail({ o, next, couriers, onMove, onEdit, onPrint, notify, askRating, onCalled, dispatch }) {
  const [busy, setBusy] = useState(false);
  const [calling, setCalling] = useState(false);
  const call = async (x) => { setCalling(true); try { await dispatch.onCall(x); } finally { setCalling(false); } };
  const [Icon, typeLabel] = TYPE[o.type] || TYPE.delivery;
  const go = async (s) => { setBusy(true); try { await onMove(o, s); } finally { setBusy(false); } };
  return (
    <section className="card-surface flex h-full min-h-[480px] flex-col overflow-hidden lg:min-h-0" data-testid="order-detail">
      <header className="flex items-center gap-3 border-b px-4 py-3">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-muted-foreground/15 text-muted-foreground"><Icon className="h-5 w-5" /></span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold" data-testid="order-detail-number">Pedido Nº {o.number}</p>
          <p className="text-xs text-muted-foreground">Feito às {fmtTime(o.created_at)} • {o.source === "cardapio" ? "Cardápio digital" : "Interno"}</p>
        </div>
        <IconBtn onClick={() => onPrint(o)} label="Reimprimir" testId={`order-reprint-${o.number}`}><Printer className="h-5 w-5" /></IconBtn>
        {!isDone(o) && <IconBtn onClick={() => go("cancelado")} label={o.status === "recebido" ? "Recusar pedido" : "Cancelar pedido"} testId={`order-refuse-${o.number}`}><X className="h-5 w-5" /></IconBtn>}
      </header>
      <p className={cn("py-2 text-center text-sm font-semibold", toneOf(o)[1])} data-testid="order-detail-status">{statusLabel(o)} {agoText(lastChange(o))}</p>
      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-4">
        <div className="flex flex-wrap gap-2"><Tag k="Tipo" v={typeLabel} /><Tag k="Canal" v={o.source === "cardapio" ? "Site" : "Interno"} /></div>
        <Block icon={User} title="Cliente">
          <p>{o.customer.name}</p>
          {o.customer.phone && <a href={waLink(o.customer.phone, "")} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-emerald-600" data-testid={`order-whatsapp-${o.number}`}>{o.customer.phone}<MessageCircle className="h-4 w-4" /></a>}
        </Block>
        <WhereBlock o={o} />
        {o.type === "delivery" && o.pickup_code && <PickupCode code={o.pickup_code} />}
        {o.type === "delivery" && dispatch?.info.module && <DispatchBox o={o} info={dispatch.info} busy={calling} onCall={call} onPause={dispatch.onPause} />}
        <Block icon={List} title="Itens do pedido">
          <ul className="space-y-1">{o.items.map((i) => <li key={i.id} className="flex justify-between gap-3"><span>{i.qty}x {i.name}{i.notes && <span className="block text-xs text-amber-600 dark:text-amber-400">obs: {i.notes}</span>}</span><span className="font-mono text-muted-foreground">{brl(i.total)}</span></li>)}</ul>
        </Block>
        {o.notes && <p className="rounded-md bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-300">Obs.: {o.notes}</p>}
        <Totals o={o} />
        {!isDone(o) && <Ops o={o} couriers={couriers} onEdit={onEdit} />}
        <Extras o={o} notify={notify} askRating={askRating} onCalled={onCalled} />
      </div>
      <DetailFooter o={o} next={next} busy={busy} go={go} onPrint={onPrint} />
    </section>
  );
}
